/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import EntityLink from '../../components/common/entity-link';
import { useEffect, useState } from 'react';

import { departmentsService } from '../../services/departments.service';
import { ministriesService } from '../../services/ministries.service';
import type { Department } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';

const DepartmentView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'departments',
    basePath: '/departments',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.DEPARTMENTS);
  const canDelete = useCanDelete(JOB_NAMES.DEPARTMENTS);

  const [data, setData] = useState<Department | null>(null);
  const [ministryName, setMinistryName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);


  // Set breadcrumb title to show department name instead of UUID
  useBreadcrumbTitle(data?.name);

  useEffect(() => {
    // Wait for ID decryption to complete
    if (!isReady || !id) {
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await departmentsService.getById(id);
        setData(res);
        if (res.ministryId) {
          try {
            const ministry = await ministriesService.getById(res.ministryId);
            setMinistryName(ministry?.name || '');
          } catch {
            setMinistryName('');
          }
        } else {
          setMinistryName('');
        }
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch department'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await departmentsService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete department'));
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Department not found'} className="mb-4 w-full" />
        <Button
          label="Back to Departments"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-Department-View">
      {/* Header Section */}
      <ScreenHeader icon="pi pi-sitemap" title="Department Details" onBack={() => navigateToList()} />

      {/* Department Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          {/* Icon */}
          <div className="w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
            <i className="pi pi-building text-blue-600 dark:text-blue-400" style={{ fontSize: '1.25rem' }} />
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold text-ink">{data.name}</h2>
              {data.departmentCd && (
                <span className="text-xs font-medium text-ink-muted bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                  {data.departmentCd}
                </span>
              )}
              {data.isDelete ? (
                <Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <p className="text-xs text-ink-muted">
              Department Configuration
            </p>
          </div>
        </div>

        {/* Edit/Delete buttons aligned with profile */}
        {!data.isDelete && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button
                label="Edit"
                icon="pi pi-pencil"
                onClick={handleEdit}
                size="small"
              />
            )}
            {canDelete && (
              <Button
                label="Delete"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteClick}
                size="small"
              />
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Department Information */}
        <div className="ds-card p-3">
          <h2 className="text-sm font-semibold text-ink mb-2">Department Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Name</label>
              <p className="text-sm text-ink">{data.name || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-muted">Department Code</label>
              <p className="text-sm text-ink mt-1">{data.departmentCd || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-ink-muted">Ministry</label>
              <div className="mt-1">
                <EntityLink type="ministry" id={(data as any).ministryId} label={ministryName} />
              </div>
            </div>
          </div>
        </div>

        <AuditInfoCard
          createdAt={data.createdAt}
          updatedAt={data.updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete Department"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
        modal
        dismissableMask
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="pi pi-exclamation-triangle text-red-600" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{data.name}</strong>?
            </p>
            <p className="text-xs text-ink-muted mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DepartmentView;
